import React, { JSX } from "react";
import { Box, Button, IconButton, Table, TableHead, TextField, useTheme } from "@mui/material";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";
import startCase from "lodash/startCase";
import produce from "immer";
import DeleteIcon from "@mui/icons-material/Delete";
import { BillingPackage } from "../../../interfaces/BillingPackages";
import {
  addBillingPackageItem,
  BillingPackageStatusType,
  ITEM_TYPES,
  removeBillItem
} from "../../../helpers/billingPackages";
import ProductSelector from "./ProductSelector";
import SummaryItemRow from "./SummaryItemRow";
import { BILLING_SOURCES } from "../../Reports/Filters";

interface Props {
  formData: BillingPackage;
  itemType: ITEM_TYPES;
  setFormData: (v: BillingPackage) => void;
}

function AdditionalItems({ formData, itemType, setFormData }: Props): JSX.Element {
  const theme = useTheme();

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }} size="small">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.action.hover
            }}
          >
            <TableCell colSpan={10} sx={{ textAlign: "center" }}>
              <Typography variant="h6">{startCase(itemType)}</Typography>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Rate</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {formData?.document?.additional
            // preserving index to access correct element even after filter
            ?.map((item, index) => ({ ...item, index }))
            ?.filter((item) => !item.isPaid)
            ?.map(({ index, ...item }) => (
              <>
                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    {item.productId ? (
                      item.description
                    ) : (
                      <ProductSelector
                        onChange={(value) => {
                          const updatedData = produce(formData, (draft) => {
                            draft.document.additional[index] = {
                              sNo: 1,
                              note: "",
                              unit: value.unit,
                              teeth: [],
                              isPaid: false,
                              source: BILLING_SOURCES.STOCKS,
                              vatPct: 0,
                              perUnit: value.unitPriceIncVAT,
                              quantity: 1,
                              subItems: null,
                              delivered: true,
                              dueAmount: value.unitPriceIncVAT,
                              netAmount: value.unitPriceIncVAT,
                              productId: value.id,
                              expiryDate: null,
                              grossTotal: value.unitPriceIncVAT,
                              paidAmount: 0,
                              description: value.name,
                              discountAmt: 0,
                              stockUnitType: "unit",
                              payingAmount: 0,
                              discountPercent: 0,
                              discountPercent: 0,
                              nonDiscountable: false,
                              calculationBasis: "perUnit",
                              serviceProviderId: "",
                              vatAmtAfterDiscount: 0,
                              vatAmtBeforeDiscount: 0
                            };
                          });
                          setFormData(updatedData);
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      variant="outlined"
                      sx={{ maxWidth: 200 }}
                      value={item.quantity}
                      onChange={(event) => {
                        const qty = Number(event.target.value) < 1 ? 1 : Number(event.target.value);

                        const updatedData = produce(formData, (draft) => {
                          draft.document.additional[index] = {
                            ...item,
                            quantity: qty,
                            netAmount: qty * item.perUnit,
                            grossTotal: qty * item.perUnit,
                            payingAmount: qty * item.perUnit
                          };
                        });
                        setFormData(updatedData);
                      }}
                    />
                  </TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell align="right">{item.perUnit}</TableCell>
                  <TableCell align="right">
                    {Number(item.quantity || 0) * Number(item.perUnit || 0)}
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={item.netAmount}
                      placeholder="Amount"
                      disabled={itemType !== ITEM_TYPES.PACKAGED}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFormData(
                          removeBillItem({
                            section: itemType,
                            data: formData,
                            itemIndex: index
                          })
                        );
                      }}
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {index === formData.document.additional.length - 1 && (
                  <TableRow sx={{ "& td, & th": { border: 0 } }}>
                    <TableCell colSpan={10}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          flexDirection: "column",
                          alignItems: "flex-end"
                        }}
                      >
                        <SummaryItemRow
                          label="Gross Total"
                          value={formData?.document?.additional
                            ?.filter((itm) => !itm.isPaid)
                            ?.reduce((prev, cur) => prev + Number(cur.grossTotal || 0), 0)
                            .toFixed(2)}
                        />
                        <SummaryItemRow
                          label="Category Total Amount"
                          value={formData?.document?.additional
                            ?.filter((itm) => !itm.isPaid)
                            ?.reduce((prev, cur) => prev + Number(cur.grossTotal || 0), 0)
                            .toFixed(2)}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          {![BillingPackageStatusType.COMPLETED, BillingPackageStatusType.CANCELED].includes(
            formData.status
          ) && (
            <TableRow>
              <TableCell colSpan={10}>
                <Button
                  onClick={() => setFormData(addBillingPackageItem({ packageData: formData }))}
                >
                  Add Item
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default AdditionalItems;
